import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  ArrowLeft, Upload, Globe, MapPin, Users, Building2,
  Layers, Save, Loader2, CheckCircle2, AlertCircle,
  Plus, X, ImageIcon, Pencil,
} from "lucide-react";
import Navbar from "../shared/Navbar";
import Footer from "../Footer";
import { COMPANY_API_END_POINT } from "@/utils/constant";
import { setSingleCompany } from "@/redux/companySlice";
// If you have a Redux action to update company, import it:
// import { updateCompany } from "@/redux/companySlice";

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const JOB_TYPE_OPTIONS = [
  "Full-Time", "Part-Time", "Internship", "Contract", "Freelance",
];

const COMPANY_TYPES = [
  "Private", "Public", "Government", "Non-Profit", "Startup", "MNC",
];

const COMPANY_SIZES = [
  "1–10 employees", "11–50 employees", "51–200 employees",
  "201–500 employees", "501–1,000 employees", "1,001–5,000 employees",
  "5,001–10,000 employees", "10,001+ employees",
];

const DEFAULT_BENEFITS = [
  "Health insurance", "Job/Soft skill training", "Cafeteria",
  "Professional degree assistance", "Office gym", "Remote work",
  "Performance bonus", "Paid time off", "Stock options",
  "Flexible hours", "Team outings",
];

/* ─────────────────────────────────────────────
   Toast component
───────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium transition-all
        ${type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}
    >
      {type === "success"
        ? <CheckCircle2 size={17} />
        : <AlertCircle size={17} />
      }
      {message}
      <button onClick={onClose} className="ml-1 opacity-70 hover:opacity-100">
        <X size={15} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Section wrapper
───────────────────────────────────────────── */
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <h2 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-3">
        {title}
      </h2>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Input helpers
───────────────────────────────────────────── */
function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-gray-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-800 placeholder-gray-400 outline-none focus:border-[#012760] focus:ring-2 focus:ring-[#012760]/10 transition-all";

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
export default function EditCompany() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const companies = useSelector((state) => state.company.allCompanies);
  const company = companies?.find(
    (c) => c._id === companyId || c.companyId === companyId
  ) || companies?.[0];

  /* ── Form state ── */
  const [form, setForm] = useState({
    companyName: "",
    tagline: "",
    description: "",
    website: "",
    location: "",
    founded: "",
    size: "",
    type: "",
    fields: [],          // industry tags
    benefits: [],
    newBenefit: "",
    newField: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const logoRef = useRef();
  const bannerRef = useRef();

  /* ── Populate form from Redux ── */
  useEffect(() => {
    if (!company) return;
    setForm({
       companyName: company.CompanyName  || "",
      tagline: company.tagline || company.description?.slice(0, 100) || "",
      description: company.description || "",
      website: company.website || "",
      location: company.location || "",
      founded: company.founded || "",
      size: company.size || "",
      type: company.type || "",
      fields: company.fields || [],
      benefits: company.benefits || [],
      newBenefit: "",
      newField: "",
    });
    if (company.logo) setLogoPreview(company.logo);
    if (company.banner) setBannerPreview(company.banner);
  }, [company]);

  /* ── Handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === "logo") { setLogoFile(file); setLogoPreview(url); }
    else { setBannerFile(file); setBannerPreview(url); }
  };

  const toggleBenefit = (b) => {
    setForm((f) => ({
      ...f,
      benefits: f.benefits.includes(b)
        ? f.benefits.filter((x) => x !== b)
        : [...f.benefits, b],
    }));
  };

  const addCustomBenefit = () => {
    const val = form.newBenefit.trim();
    if (!val || form.benefits.includes(val)) return;
    setForm((f) => ({ ...f, benefits: [...f.benefits, val], newBenefit: "" }));
  };

  const removeBenefit = (b) => {
    setForm((f) => ({ ...f, benefits: f.benefits.filter((x) => x !== b) }));
  };

 const addIndustryField = () => {
  const val = form.newField.trim();
  if (!val || form.fields.includes(val)) return;
  setForm((f) => ({ ...f, fields: [...f.fields, val], newField: "" }));
};

  const removeField = (f_) => {
    setForm((f) => ({ ...f, fields: f.fields.filter((x) => x !== f_) }));
  };

  /* ── Submit ── */
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!form.companyName.trim()) {
    setToast({ message: "Company name is required.", type: "error" });
    return;
  }

  setIsSaving(true);
  try {
    const formData = new FormData();
    const textFields = [
      "companyName", "tagline", "description",
      "website", "location", "founded", "size", "type",
    ];
    textFields.forEach((key) => formData.append(key, form[key]));
    formData.append("fields", JSON.stringify(form.fields));
    formData.append("benefits", JSON.stringify(form.benefits));
    if (logoFile) formData.append("logo", logoFile);
    if (bannerFile) formData.append("banner", bannerFile);

    const res = await axios.put(
      `${COMPANY_API_END_POINT}/update/${companyId}`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    if (res.data.status) {               // ← was res.data.success
      dispatch(setSingleCompany(res.data.company));  // ← update Redux
      setToast({ message: "Company updated successfully!", type: "success" });
      setTimeout(() => navigate(-1), 1500);
    } else {
      throw new Error(res.data.message || "Update failed");
    }
  } catch (err) {
    setToast({
      message: err?.response?.data?.message || err.message || "Something went wrong.",
      type: "error",
    });
  } finally {
    setIsSaving(false);
  }
};
  /* ── UI ── */
  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pt-24 pb-16">

        {/* Header */}
        <div className="flex items-center gap-4 mb-7">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#012760] hover:border-[#012760] transition-colors bg-white"
          >
            <ArrowLeft size={17} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Edit Company Profile</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              Update your company information visible to job seekers
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── BANNER & LOGO ── */}
          <Section title="Brand & Media">
            {/* Banner */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-2">
                Company Banner
              </label>
              <div
                className="relative w-full h-40 rounded-xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50 cursor-pointer group hover:border-[#012760] transition-colors"
                onClick={() => bannerRef.current?.click()}
              >
                {bannerPreview ? (
                  <>
                    <img
                      src={bannerPreview}
                      alt="Banner"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-semibold flex items-center gap-2">
                        <Upload size={15} /> Change Banner
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
                    <ImageIcon size={28} />
                    <span className="text-xs">Click to upload banner (recommended 1600×400)</span>
                  </div>
                )}
                <input
                  ref={bannerRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageChange(e, "banner")}
                />
              </div>
            </div>

            {/* Logo */}
            <div className="flex items-start gap-5 mt-2">
              <div
                className="relative w-20 h-20 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 overflow-hidden cursor-pointer group hover:border-[#012760] transition-colors flex-shrink-0"
                onClick={() => logoRef.current?.click()}
              >
                {logoPreview ? (
                  <>
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-full h-full object-contain p-1"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl">
                      <Pencil size={15} className="text-white" />
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-1 text-gray-400">
                    <Upload size={18} />
                    <span className="text-[10px] text-center leading-tight">Logo</span>
                  </div>
                )}
                <input
                  ref={logoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageChange(e, "logo")}
                />
              </div>
              <div className="text-sm text-gray-500 pt-2">
                <p className="font-semibold text-gray-700 text-xs">Company Logo</p>
                <p className="text-xs mt-1 text-gray-400">
                  Square image recommended (min 200×200px). PNG or JPG.
                </p>
              </div>
            </div>
          </Section>

          {/* ── BASIC INFO ── */}
          <Section title="Basic Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company Name" required>
                <input
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  placeholder="e.g. Acme Corp"
                  className={inputCls}
                />
              </Field>

              <Field label="Website">
                <div className="relative">
                  <Globe size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://www.yourcompany.com"
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </Field>
            </div>

            <Field label="Tagline">
              <input
                name="tagline"
                value={form.tagline}
                onChange={handleChange}
                placeholder="Short one-liner about your company"
                maxLength={120}
                className={inputCls}
              />
              <p className="text-[11px] text-gray-400 mt-1">{form.tagline.length}/120</p>
            </Field>

            <Field label="About / Description">
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                placeholder="Describe what your company does, its mission and culture…"
                className={`${inputCls} resize-none`}
              />
            </Field>
          </Section>

          {/* ── COMPANY DETAILS ── */}
          <Section title="Company Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Headquarters / Location">
                <div className="relative">
                  <MapPin size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. New York, USA"
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </Field>

              <Field label="Founded Year">
                <div className="relative">
                  <Building2 size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    name="founded"
                    value={form.founded}
                    onChange={handleChange}
                    placeholder="e.g. 1994"
                    maxLength={4}
                    className={`${inputCls} pl-9`}
                  />
                </div>
              </Field>

              <Field label="Company Size">
                <div className="relative">
                  <Users size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select
                    name="size"
                    value={form.size}
                    onChange={handleChange}
                    className={`${inputCls} pl-9 appearance-none`}
                  >
                    <option value="">Select size</option>
                    {COMPANY_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </Field>

              <Field label="Company Type">
                <div className="relative">
                  <Layers size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className={`${inputCls} pl-9 appearance-none`}
                  >
                    <option value="">Select type</option>
                    {COMPANY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>
          </Section>

          {/* ── INDUSTRY FIELDS ── */}
          <Section title="Industry / Fields">
            <p className="text-xs text-gray-400 -mt-2">
              Tags shown on your company profile (e.g. FinTech, B2B, SaaS)
            </p>
            <div className="flex flex-wrap gap-2">
              {form.fields.map((f) => (
                <span
                  key={f}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs font-medium"
                >
                  {f}
                  <button
                    type="button"
                    onClick={() => removeField(f)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-1">
              <input
                value={form.newField}
                onChange={(e) => setForm((f) => ({ ...f, newField: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIndustryField())}
                placeholder="Add a field (press Enter)"
                className={`${inputCls} flex-1`}
              />
              <button
                type="button"
                onClick={addIndustryField}
                className="px-4 py-2.5 rounded-lg bg-[#012760] text-white text-sm font-semibold hover:bg-[#01184a] transition-colors flex items-center gap-1.5"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </Section>

          {/* ── BENEFITS ── */}
          <Section title="Employee Benefits">
            <p className="text-xs text-gray-400 -mt-2">
              Select all benefits your company offers
            </p>

            {/* Preset options */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {DEFAULT_BENEFITS.map((b) => {
                const selected = form.benefits.includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => toggleBenefit(b)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all text-left
                      ${selected
                        ? "border-[#012760] bg-blue-50 text-[#012760]"
                        : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                      }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
                        ${selected ? "border-[#012760] bg-[#012760]" : "border-gray-300"}`}
                    >
                      {selected && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    {b}
                  </button>
                );
              })}
            </div>

            {/* Custom added benefits */}
            {form.benefits.filter((b) => !DEFAULT_BENEFITS.includes(b)).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {form.benefits
                  .filter((b) => !DEFAULT_BENEFITS.includes(b))
                  .map((b) => (
                    <span
                      key={b}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 text-xs font-medium"
                    >
                      {b}
                      <button
                        type="button"
                        onClick={() => removeBenefit(b)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
              </div>
            )}

            {/* Add custom benefit */}
            <div className="flex gap-2 mt-1">
              <input
                value={form.newBenefit}
                onChange={(e) => setForm((f) => ({ ...f, newBenefit: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomBenefit())}
                placeholder="Add a custom benefit…"
                className={`${inputCls} flex-1`}
              />
              <button
                type="button"
                onClick={addCustomBenefit}
                className="px-4 py-2.5 rounded-lg bg-[#012760] text-white text-sm font-semibold hover:bg-[#01184a] transition-colors flex items-center gap-1.5"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </Section>

          {/* ── ACTIONS ── */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#012760] text-white text-sm font-bold hover:bg-[#01184a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <><Loader2 size={15} className="animate-spin" /> Saving…</>
              ) : (
                <><Save size={15} /> Save Changes</>
              )}
            </button>
          </div>
        </form>
      </div>

      <Footer />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
